import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { LogIn, Lock, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import useSeo from '../hooks/useSeo';

export default function AdminLogin() {
  const { user, login, loading } = useAuth();
  useSeo({ title: 'Admin Login | Pusha Teez', isPrivate: true });
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate('/admin', { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6">
      <div className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 20%, #00b8ff22, transparent 40%), radial-gradient(circle at 80% 80%, #00b8ff15, transparent 40%)',
        }}
      />
      <div className="relative w-full max-w-md">
        <div className="text-center mb-10">
          <img
            src="https://customer-assets.emergentagent.com/job_pusha-rebrand/artifacts/5rw0r3ms_PTZ-LOGO-BLK.png"
            alt="Pusha Teez"
            className="w-16 h-16 mx-auto mb-6 invert"
          />
          <h1 className="font-display text-[#f4efe6] uppercase text-4xl md:text-5xl tracking-tight">
            Admin <span className="font-script text-[#00b8ff] normal-case italic">Console</span>
          </h1>
          <p className="text-[#cfc8bb] text-sm mt-3">Sign in to manage quotes, blogs and content.</p>
        </div>

        <form onSubmit={onSubmit} className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-8 space-y-5">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#00b8ff] mb-2" htmlFor="admin-email">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a5a5a]" />
              <input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                className="w-full bg-[#0f0f0f] border border-white/10 rounded-lg pl-11 pr-4 py-3 text-[#f4efe6] placeholder:text-[#5a5a5a] focus:outline-none focus:border-[#00b8ff] focus:ring-2 focus:ring-[#00b8ff]/20 transition-colors"
                placeholder="you@pushateez.com"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#00b8ff] mb-2" htmlFor="admin-password">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a5a5a]" />
              <input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                className="w-full bg-[#0f0f0f] border border-white/10 rounded-lg pl-11 pr-4 py-3 text-[#f4efe6] placeholder:text-[#5a5a5a] focus:outline-none focus:border-[#00b8ff] focus:ring-2 focus:ring-[#00b8ff]/20 transition-colors"
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-400">{error}</p>
          )}

          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#00b8ff] text-black rounded-full px-6 py-3.5 font-semibold text-[15px] hover:bg-[#f4efe6] transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <LogIn className="w-4 h-4" />
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
